import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config.service';
import { ExpenseReportReportData, ExpenseReportReportWhereParams, FinanceStats, PaymentProgressStats, PaymentStats } from './types/finance';
import { map, Observable, switchMap } from 'rxjs';
import { DataTransform, DateField } from './dataTransformFunctions';
import { Overpayment } from './commonInterfaces'
import { query } from '@angular/animations';


@Injectable({
  providedIn: 'root'
})
export class FinanceService {

  constructor(
    private http: HttpClient,
    private config: ConfigService
  ) { }

  expenseReportReportDateFields: DateField<ExpenseReportReportData>[] = [
    { fieldName: 'tripStartDate', dateType: 'local' },
    { fieldName: 'tripEndDate', dateType: 'local' },
    { fieldName: 'completedDate', dateType: 'local' },
    { fieldName: 'tlCompleteTimestamp', dateType: 'local' },
    { fieldName: 'llReviewTimestamp', dateType: 'local' }
  ]

  expenseReportReportTripsDateFields: DateField<ExpenseReportReportData['trips'][number]>[] = [
    { fieldName: 'tripDate', dateType: 'local' }
  ]

  financeStatsDateFields: DateField<FinanceStats>[] = [
    { fieldName: 'tripDate', dateType: 'local' },
    { fieldName: 'regDate', dateType: 'local' },
    { fieldName: 'dueDate', dateType: 'local' },
    { fieldName: 'lastCommDate', dateType: 'central' },

  ]

  overpaymentDateFields: DateField<Overpayment>[] = [
    { fieldName: 'tripDate', dateType: 'local' }
  ]

  getPaymentStatsGraphData(year?: number) {
    const params = year ? `?year=${year}` : '';
    return this.http.get<PaymentStats[]>(`${this.config.dataApiUrl}/finance-reports/monthly-payment-stats${params}`) ?? [];
  }

  getPaymentProgress(year?: number) {
    const params = year ? `?year=${year}` : '';
    return this.http.get<PaymentProgressStats>(`${this.config.dataApiUrl}/finance-reports/payment-progress${params}`) ?? [];
  }

  getFinanceStats(reportType: string) {
    return this.http.get<FinanceStats[]>(`${this.config.dataApiUrl}/finance-reports/finance-stats?reportType=${reportType}`).pipe(
      switchMap(data => DataTransform.replaceDatesArray(data, this.financeStatsDateFields))
    );
  }

  getExpenseReportReportData(params: ExpenseReportReportWhereParams): Observable<ExpenseReportReportData[]> {

    return this.http.post<ExpenseReportReportData[]>(
      `${this.config.dataApiUrl}/finance-reports/expense-report-report`,
      params
    ).pipe(
      map(data => {
        for (const item of data) {
          DataTransform.replaceDatesArrayDirect(item.trips, this.expenseReportReportTripsDateFields)
        }
        return data
      }),
      switchMap(data => DataTransform.replaceDatesArray(data, this.expenseReportReportDateFields))
    )
  }

  syncExpenseReport(docId: string) {
    return this.http.post<void>(
      `${this.config.dataApiUrl}/expense-reports/sync-expense-report/${docId}`,
      {}
    )
  }

  getOverpayments(startDate?: Date | null, endDate?: Date | null): Observable<Overpayment[]> {
    
    const params: string[] = [];
    
    if (startDate) {
      params.push(`startDate=${encodeURIComponent(startDate.toISOString())}`);
    }
    
    if (endDate) {
      params.push(`endDate=${encodeURIComponent(endDate.toISOString())}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    
    return this.http.get<Overpayment[]>(
      `${this.config.dataApiUrl}/trips/overpayments${queryString}`
    ).pipe(
      switchMap(data => DataTransform.replaceDatesArray(data, this.overpaymentDateFields))
    );
  }

}